import _ from "lodash";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, ElementsConsumer } from "@stripe/react-stripe-js";
import PropTypes from "prop-types";
import React from "react";
import { Divider, Icon, IconButton } from "rsuite";
import { PriceHoc } from "../../umbilical_hocs";
import { createUseStyles } from "react-jss";
import { InlineIcon } from "@iconify/react";
import jssVariables from "../../jss_setup";

class CheckoutForm extends React.Component {
  constructor(props) {
    super(props);

    this.cardStyle = {
      style: {
        base: {
          color: "#32325d",
          fontFamily: "Arial, sans-serif",
          fontSmoothing: "antialiased",
          fontSize: "16px",
          "::placeholder": {
            color: "#32325d",
          },
        },
        invalid: {
          color: "#fa755a",
          iconColor: "#fa755a",
        },
      },
    };

    this.handleSubmit = this.handleSubmit.bind(this);
  }
  async handleSubmit(event) {
    const { setLoading, setIdle, paymentIntent, paymentFailed, paymentSuccess, savePayment } =
      this.props;

    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    const { stripe, elements } = this.props;

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make  sure to disable form submission until Stripe.js has loaded.
      return;
    }

    await setLoading();
    const result = await stripe.confirmCardPayment(paymentIntent.client_secret, {
      payment_method: {
        card: elements.getElement(CardElement),
      },
    });
    setIdle();

    savePayment(result);

    if (result.error) {
      // Show error to your customer (e.g., insufficient funds)
      paymentFailed();
    } else {
      // The payment has been processed!
      if (result.paymentIntent.status === "succeeded") {
        // Show a success message to your customer
        // There's a risk of the customer closing the window before callback
        // execution. Set up a webhook or plugin to listen for the
        // payment_intent.succeeded event that handles any business critical
        // post-payment actions.
        paymentSuccess();
        return;
      }
      paymentFailed();
    }
  }
  render() {
    const { loading, onBack } = this.props;
    //const { t } = useContext(TranslationContext);

    return (
      <form className="PaymentForm__form" onSubmit={this.handleSubmit}>
        {loading ? (
          <div
            style={{
              display: "grid",
              gridGap: jssVariables.normal_gap,
              placeItems: "center",
              textAlign: "center",
            }}
          >
            <InlineIcon icon="eos-icons:loading" style={{ fontSize: "xxx-large" }} />
            <h5>Please wait....</h5>
          </div>
        ) : null}
        <CardElement
          className="PaymentForm__form__card-element"
          id="card-element"
          options={this.cardStyle}
        />
        <div className="PaymentForm__form__actions">
          <IconButton
            icon={<Icon icon="angle-left" />}
            placement="left"
            appearance="ghost"
            onClick={onBack}
          >
            {/* <strong>{t("back")}</strong> */}
            <strong>BACK</strong>{" "}
          </IconButton>
          <IconButton
            className="PaymentForm__form__paybtn"
            icon={<Icon icon="credit-card" />}
            placement="right"
            appearance="primary"
            onClick={this.handleSubmit}
          >
            <strong>Pay and Book</strong>
          </IconButton>
        </div>
      </form>
    );
  }
}
CheckoutForm.propTypes = {
  stripe: PropTypes.object,
  elements: PropTypes.object,
  paymentIntent: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired,
  setIdle: PropTypes.func.isRequired,
  savePayment: PropTypes.func.isRequired,
  paymentFailed: PropTypes.func.isRequired,
  paymentSuccess: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};
const priceStyles = createUseStyles({
  main: {
    color: (props) => props.baseColor,
    textAlign: "center",
    border: (props) => `4px solid ${props.baseColor}`,
    borderRadius: "50%",
    height: "15rem",
    width: "15rem",
    lineHeight: "15rem",
    justifySelf: "center",
  },
});

var Price = (props) => {
  const { price, currencySymbol, baseColor } = props;

  const classes = priceStyles({ baseColor });

  return (
    <h1 className={classes.main}>
      {currencySymbol}
      {price}
    </h1>
  );
};
Price.propTypes = {
  price: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  baseColor: PropTypes.string.isRequired,
};
Price = PriceHoc(Price);

const styles = createUseStyles({
  main: {
    display: "grid",
    alignItems: "center",
    height: "100%",
    gridTemplateRows: "1fr auto auto",
  },
});
const PaymentForm = (props) => {
  const {
    loading,
    price,
    currency,
    paymentIntent,
    baseColor,
    onBack,
    setLoading,
    setIdle,
    savePayment,
    paymentFailed,
    paymentSuccess,
  } = props;

  // eslint-disable-next-line no-undef
  var stripe_key = import.meta.env.VITE_APP_STRIPE_KEY;

  const promise = loadStripe(stripe_key);

  const classes = styles();

  return (
    <div className={classes.main}>
      <Price
        price={_.get(paymentIntent, "amount", price)}
        currency={_.get(paymentIntent, "currency", currency)}
        baseColor={baseColor}
      />
      <Divider />
      <Elements stripe={promise}>
        <ElementsConsumer>
          {({ elements, stripe }) => (
            <CheckoutForm
              loading={loading}
              elements={elements}
              stripe={stripe}
              paymentIntent={paymentIntent}
              onBack={onBack}
              setLoading={setLoading}
              setIdle={setIdle}
              savePayment={savePayment}
              paymentFailed={paymentFailed}
              paymentSuccess={paymentSuccess}
            />
          )}
        </ElementsConsumer>
      </Elements>
    </div>
  );
};
PaymentForm.defaultProps = {
  baseColor: "",
};
PaymentForm.propTypes = {
  baseColor: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  price: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  paymentIntent: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  setIdle: PropTypes.func.isRequired,
  savePayment: PropTypes.func.isRequired,
  paymentFailed: PropTypes.func.isRequired,
  paymentSuccess: PropTypes.func.isRequired,
};

export default PaymentForm;
